import { supabase } from './supabase';

// Issue #394, Session 78n7. The Ledger's export.
//
// In plain terms: a manager filters the Ledger to the branch, the month and the
// transaction type they care about, presses Export, and gets a file. Three
// things were wrong with that file and a reader could see none of them --
// it could be silently incomplete, it carried no cost at all, and it did not
// say what it was.
//
// Approved gate: docs/gates/Stede_DocumentOutput_Gate_395.html, decisions
// D2, D5, D7, D8, D9, D12.
//
// This module owns fetching and shaping only. It deliberately imports nothing
// from InventoryLedgerPage.js -- the page imports this, and the reverse would
// be a cycle -- so anything display-related (branch names, transaction type
// labels) is passed in.

// A page size comfortably at or below any plausible PostgREST ceiling.
export const EXPORT_CHUNK = 1000;

// Above this, the export refuses rather than trying. Gate D5: "if it cannot be
// delivered, refuse with the number. Never silently truncate." The number is a
// browser-memory judgement, not a server limit -- SAP's own Fiori guidance
// warns that large exports exhaust browser memory, and a PDF of 25,000 rows is
// not a document anybody reads.
export const EXPORT_HARD_CAP = 25000;

function applyFilters(query, { filters, canSeeAllBranches, userBranchId, start, end }) {
  if (!canSeeAllBranches) {
    query = query.eq('branch_id', userBranchId);
  } else if (filters.branchId) {
    query = query.eq('branch_id', filters.branchId);
  }
  if (filters.txType) query = query.eq('tx_type', filters.txType);
  if (filters.userId) query = query.eq('user_id', filters.userId);
  if (start) query = query.gte('created_at', start);
  if (end) query = query.lt('created_at', end);
  return query;
}

// How many rows the active filters match, WITHOUT fetching them.
//
// This is the count the dialog states before anything is written, and gate
// callout E is the whole reason it exists: the count used to be printed on the
// finished PDF, where it agreed with the truncated set and therefore proved
// nothing. Stated before the file is made, it is a promise; printed after, it
// is a description of whatever happened to arrive.
//
// Note this counts the SERVER-side filters only. The search box is applied in
// the browser (see below), so when a search is active this is an upper bound --
// which is exactly what the too-large check wants.
export async function countForExport(opts) {
  let query = supabase
    .from('inventory_transactions')
    .select('id', { count: 'exact', head: true });
  query = applyFilters(query, opts);
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

// Fetch every matching row, paging until the set is complete.
//
// THIS IS THE FIX FOR #394's ITEM 1, AND IT DOES NOT NEED TO KNOW THE SERVER'S
// ROW CEILING. The old code applied no .range() and no .limit() at all, so
// PostgREST's own max-rows quietly capped the result and produced a
// well-formed, professional, incomplete financial document.
//
// The ceiling turned out not to be knowable: it is not set as a role setting on
// either database, and neither has enough rows to reveal it empirically (see
// db/migrations/20260830_export_audit_trail.sql). So this SELF-CALIBRATES
// instead of assuming a number. It asks for EXPORT_CHUNK rows; if fewer come
// back while more are known to remain, the server's ceiling is smaller than the
// chunk, and whatever did arrive becomes the step for the next request. That is
// correct against a ceiling of 1000, of 500, of none, and of one changed later.
//
// It also fixes something the gate found and the issue entry had not: the old
// export selected a NARROWER column set than the on-screen query and never
// fetched cost at all, so cost was not merely missing from the output -- it was
// never asked for.
export async function fetchAllForExport(opts, knownTotal) {
  const { withCost } = opts;

  // The count is the loop's terminating condition, so it is read first rather
  // than inferred from a short page -- a short page is ambiguous between
  // "that's everything" and "the server capped you", and those need different
  // responses.
  const total = knownTotal != null ? knownTotal : await countForExport(opts);

  // Cost columns are only ever REQUESTED when the person may see them. Gate D7
  // -- if the columns are fetched and then dropped at render time, the report
  // button becomes the way around the permission the moment anybody touches
  // this code again.
  const costColumns = withCost ? ', unit_cost_at_transaction, unit_cost_settled' : '';

  const rows = [];
  let offset = 0;
  let step = EXPORT_CHUNK;

  while (rows.length < total && rows.length < EXPORT_HARD_CAP) {
    let query = supabase
      .from('inventory_transactions')
      .select(`
        id, tx_type, qty, qty_after, work_order_ref, work_order_ref_note, notes,
        transfer_number, counterparty_branch_id, request_number,
        created_at, branch_id${costColumns},
        inventory_items ( id, name, sku ),
        users ( id, full_name )
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + step - 1);
    query = applyFilters(query, opts);

    const { data, error } = await query;
    if (error) throw error;

    const batch = data || [];

    // An empty page is the only unambiguous "there is nothing more". It also
    // guarantees termination: every other path advances `offset` by at least
    // one row.
    if (batch.length === 0) break;

    rows.push(...batch);

    // A page shorter than the one requested means either the set ran out --
    // in which case the `rows.length < total` condition ends the loop on the
    // next pass -- or the server's ceiling is smaller than the step. The two
    // are indistinguishable from here and need the same response: treat what
    // actually arrived as the real page size and carry on from where it ended.
    // THIS is what makes the export correct without knowing the ceiling.
    if (batch.length < step) step = batch.length;
    offset += batch.length;
  }

  return rows;
}

// #633, Session 79.22b -- WHICH DOCUMENT A MOVEMENT BELONGS TO. The Ledger
// showed the work order and nothing else, so the two movements that involve
// another party - a transfer and a delivery - named neither the party nor the
// document, while both sat on the row. One rule for the desk table, the phone
// card and the detail panel, so they cannot disagree. Returns '' when the row
// carries nothing; the caller draws its own dash.
export function ledgerReference(tx, branchName = () => '') {
  const parts = [];
  const other = tx?.counterparty_branch_id != null ? (branchName(tx.counterparty_branch_id) || '') : '';
  // The three movements that carry the other branch, each in its own words.
  const how = tx?.tx_type === 'transfer_in' ? 'from' : tx?.tx_type === 'production_in' ? 'for' : 'to';
  if (tx?.transfer_number) parts.push(other ? `${tx.transfer_number} \u00b7 ${how} ${other}` : tx.transfer_number);
  else if (other) parts.push(`${how} ${other}`);
  if (tx?.request_number) parts.push(tx.request_number);
  if (tx?.work_order_ref) parts.push(parts.length ? `WO ${tx.work_order_ref}` : tx.work_order_ref);
  return parts.join(' \u00b7 ');
}

// #788, Session 79.22b.6.1g -- a movement's words live in two columns. Receive
// and Use write the job note (work_order_ref_note); Adjust writes its reason,
// note and "Adjusted by" to notes, which nothing read, so every adjustment's
// reason was saved and never shown. Both, in that order, wherever a note shows.
export function movementNote(tx) {
  return [tx?.work_order_ref_note, tx?.notes]
    .map(v => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
    .join(' · ');
}

// The search box has always been applied in the browser rather than by the
// server, and the gate flagged that as compounding the truncation: a capped
// export searched only within the first thousand rows. Paging the full set
// fixes that as a side effect -- this now filters everything the filters match,
// not everything that happened to arrive.
export function applySearch(rows, search) {
  if (!search) return rows;
  const q = search.toLowerCase();
  return rows.filter(tx =>
    tx.inventory_items?.name?.toLowerCase().includes(q) ||
    tx.inventory_items?.sku?.toLowerCase().includes(q) ||
    tx.work_order_ref?.toLowerCase().includes(q) ||
    // #633 -- and the documents the row now names.
    tx.transfer_number?.toLowerCase().includes(q) ||
    tx.request_number?.toLowerCase().includes(q)
  );
}

// #343's three cost states, kept distinguishable. Gate D8.
//
// The screen renders these as one string with an arrow ("$4.00 → $4.25"),
// which is right for reading and wrong for a file: an accountant needs a number
// they can sum. So the export splits them into separate machine-readable
// columns and names the state outright, rather than flattening three states
// into one figure or dropping the distinction the previous session built.
export function costFields(tx) {
  const basis = tx?.unit_cost_at_transaction;
  const settled = tx?.unit_cost_settled;
  const hasBasis = basis != null && Number.isFinite(Number(basis));
  const hasSettled = settled != null && Number.isFinite(Number(settled));

  if (hasSettled) {
    return {
      state: 'Settled',
      effective: Number(settled),
      booked: hasBasis ? Number(basis) : null,
    };
  }
  if (hasBasis) {
    return { state: 'Provisional', effective: Number(basis), booked: Number(basis) };
  }
  return { state: 'No cost recorded', effective: null, booked: null };
}

function money(n) {
  if (n == null) return '';
  return Number(n).toFixed(2);
}

function signedQty(qty, txType) {
  const n = Number(qty);
  // #371 D23. A fourth reader of the movement type, and the one most easily
  // missed: without this a production row exports UNSIGNED, so the running
  // balance in a downloaded ledger disagrees with the one on screen.
  if (txType === 'receive' || txType === 'transfer_in' || txType === 'production_in') return Math.abs(n);
  if (txType === 'consume' || txType === 'transfer_out') return -Math.abs(n);
  return n;
}

// ── Layout 1: movement detail ────────────────────────────────────────────────
// "Every transaction, one row each -- who moved what, when, and what it cost."
export function buildMovementTable(rows, { branchName, txLabel, withCost, fmtDate }) {
  const headers = [
    'Date', 'Type', 'Item', 'SKU', 'Branch', 'Job Ref', 'Notes',
    // #633 -- separate columns rather than the screen's one sentence, so an
    // accountant can sort and match on them.
    'Transfer', 'Request', 'Other branch',
    'User', 'Qty', 'After',
  ];
  if (withCost) headers.push('Unit cost', 'Cost state', 'Cost booked at receive', 'Extended value');

  const body = rows.map(tx => {
    const qty = signedQty(tx.qty, tx.tx_type);
    const line = [
      fmtDate(tx.created_at),
      txLabel(tx.tx_type, tx),
      tx.inventory_items?.name || '',
      tx.inventory_items?.sku || '',
      branchName(tx.branch_id),
      tx.work_order_ref || '',
      movementNote(tx),
      tx.transfer_number || '',
      tx.request_number || '',
      tx.counterparty_branch_id != null ? (branchName(tx.counterparty_branch_id) || '') : '',
      tx.users?.full_name || '',
      qty,
      tx.qty_after ?? '',
    ];
    if (withCost) {
      const c = costFields(tx);
      line.push(
        money(c.effective),
        c.state,
        money(c.booked),
        c.effective == null ? '' : money(Math.abs(qty) * c.effective),
      );
    }
    return line;
  });

  return { headers, body };
}

// ── Layout 2: valuation ──────────────────────────────────────────────────────
// Gate D2. The research finding is that these are genuinely two documents:
// an accountant wants valuation -- per item, what moved and what it is worth --
// while a manager wants movement detail. NetSuite, Fishbowl and SAP all ship
// them as separate reports in separate menus; the beat is not shipping both, it
// is not making the reader know which one to go and find.
//
// Opening and closing quantities are DERIVED rather than stored, and the
// derivation is stated because it is the sort of thing a later session would
// otherwise have to reverse-engineer: every transaction carries qty_after, so
// within the filtered range the closing balance is the qty_after of an item's
// most recent movement, and the opening balance is that of its earliest
// movement minus the quantity that movement itself applied.
export function buildValuationTable(rows, { withCost }) {
  const headers = [
    'Item', 'SKU', 'Opening qty', 'Received', 'Consumed', 'Net change', 'Closing qty', 'Movements',
  ];
  if (withCost) headers.push('Closing value', 'Costed movements', 'Uncosted movements');

  // Rows arrive newest-first.
  const byItem = new Map();
  for (const tx of rows) {
    const key = tx.inventory_items?.id ?? `unknown:${tx.inventory_items?.sku || ''}`;
    if (!byItem.has(key)) {
      byItem.set(key, {
        name: tx.inventory_items?.name || '(item removed)',
        sku: tx.inventory_items?.sku || '',
        received: 0, consumed: 0, movements: 0,
        newest: tx, oldest: tx,
        costed: 0, uncosted: 0, lastCost: null,
      });
    }
    const g = byItem.get(key);
    g.movements += 1;
    const q = signedQty(tx.qty, tx.tx_type);
    // #694 -- AN UNDO NETS AGAINST WHAT IT CANCELS, as the Ledger's own totals
    // do: an undone sign-off (negative) comes off Received and an undone send
    // (positive) off Consumed. Sorted by sign alone it would read "Received 30,
    // Consumed 30" for a press that was taken back.
    if (tx.tx_type === 'reversal') {
      if (q < 0) g.received += q; else g.consumed -= q;
    } else if (q >= 0) g.received += q; else g.consumed += Math.abs(q);
    g.oldest = tx; // newest-first, so the last one seen is the oldest
    const c = costFields(tx);
    if (c.effective == null) g.uncosted += 1;
    else { g.costed += 1; if (g.lastCost == null) g.lastCost = c.effective; }
  }

  const body = [...byItem.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(g => {
      const closing = g.newest.qty_after ?? null;
      const openingBase = g.oldest.qty_after;
      const opening = openingBase == null
        ? null
        : Number(openingBase) - signedQty(g.oldest.qty, g.oldest.tx_type);
      const net = g.received - g.consumed;
      const line = [
        g.name, g.sku,
        opening ?? '', g.received, g.consumed, net, closing ?? '', g.movements,
      ];
      if (withCost) {
        line.push(
          closing != null && g.lastCost != null ? money(Number(closing) * g.lastCost) : '',
          g.costed,
          g.uncosted,
        );
      }
      return line;
    });

  return { headers, body };
}

// ── The self-describing header block ─────────────────────────────────────────
// Gate D12, and issue item 4. The PDF already states its provenance on its
// face; the CSV was bare headers and rows, so six months later nobody could
// tell whether a file was Dallas or every branch, this month or last, receipts
// or everything. A file that cannot describe itself cannot be relied on as a
// record.
//
// The "cost omitted because..." line matters as much as the rest: gate callout
// F. A blank column reads as a bug; a stated permission reads as a decision.
export function describeExport({
  companyName, layout, branchLabel, dateLabel, txLabel, userLabel, search,
  rowCount, withCost, canSeeCostAtAll, generatedBy, generatedAt,
}) {
  const lines = [
    [companyName],
    [layout === 'valuation' ? 'Inventory valuation' : 'Transaction ledger — movement detail'],
    [],
    ['Branch', branchLabel],
    ['Date range', dateLabel],
    ['Transaction type', txLabel || 'All types'],
    ['User', userLabel || 'All users'],
    ['Search', search || '(none)'],
    ['Rows in this file', String(rowCount)],
    ['Cost columns', withCost
      ? 'Included'
      : (canSeeCostAtAll ? 'Excluded by choice' : 'Not available to you — omitted by permission')],
    ['Generated by', generatedBy],
    ['Generated', generatedAt],
  ];

  // State the valuation BASIS on the file, because "Closing value" implies a
  // method and this one is not the method an accountant assumes. Closing value
  // is closing quantity times the most recent KNOWN unit cost inside the
  // filtered range -- it is not FIFO, not a weighted average, and not a
  // period-end revaluation. Stede does not hold enough cost history to compute
  // those honestly, and a number labelled only "Closing value" would be taken
  // for one of them. An unstated basis is the same class of defect as the
  // unstated row count this file exists to fix.
  if (layout === 'valuation' && withCost) {
    lines.push(['Valuation basis',
      'Closing quantity x most recent known unit cost within the range. Not FIFO or weighted average.']);
    lines.push(['Uncosted movements',
      'Counted per item. An item whose movements carry no cost has no closing value, shown blank rather than zero.']);
  }

  lines.push([]);
  return lines;
}

export function toCsv(blockRows, headers, body) {
  const esc = cell => `"${String(cell ?? '').replace(/"/g, '""')}"`;
  const all = [...blockRows.map(r => r.map(esc).join(',')), headers.map(esc).join(','),
    ...body.map(r => r.map(esc).join(','))];
  // The BOM keeps Excel from mangling accented names, and was already here.
  return '﻿' + all.join('\r\n');
}

// ── The audit trail ──────────────────────────────────────────────────────────
// Gate D9. Written AFTER the file is produced, so an export that failed is not
// recorded as one that happened -- and best-effort, because failing to log must
// never cost somebody the file they asked for. A missing log line is a gap; a
// refused export because the log was unreachable is a broken feature.
export async function logExport({ profile, layout, format, filters, rowCount, includedCost }) {
  try {
    await supabase.from('export_log').insert({
      exported_by: profile?.id || null,
      exported_by_name_snapshot: profile?.full_name || 'Unknown',
      surface: 'inventory_ledger',
      layout,
      format,
      filters,
      row_count: rowCount,
      included_cost: !!includedCost,
    });
  } catch {
    /* best-effort, deliberately */
  }
}

// ── The Ledger's cost rule, moved here from InventoryLedgerPage.js ─────────────
// #624, Session 79.22b: moved without changing a line, so the page, the export
// and the detail panel read ONE rule. Exported so it is tested directly.

export function fmtCost(cost) {
  if (cost === null || cost === undefined) return '—';
  return '$' + Number(cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// #343, Session 78m. D9 -- the Ledger declares when a cost is PROVISIONAL.
//
// The sharpest sentence in #343's entry is that the wrong number is invisible:
// a receipt books a guess at what something cost, the invoice later says what it
// really cost, and nothing on any screen ever indicated the first figure was an
// estimate. A correction that is equally invisible fixes the arithmetic and not
// the trust problem -- so a row now reads "$42.50 -> $47.20" and explains itself.
//
// Three states, and the third is the one people forget:
//
//   none         no cost was ever recorded. Common at D&R, because a packing slip
//                usually carries no price. NOT an error, and not styled as one.
//   provisional  a cost was booked at receive and no invoice has settled it yet.
//                Amber because it is PENDING, not because anything is wrong --
//                gate callout C. The red treatment is reserved for discrepancies.
//   settled      an invoice arrived and the real cost is known. Both numbers are
//                shown, because hiding the original would be the silent rewrite
//                D1 exists to prevent.
//
// Exported and pure so the three states can be tested without rendering a table.
export function ledgerCostState(tx) {
  const basis = tx?.unit_cost_at_transaction;
  const settled = tx?.unit_cost_settled;
  const hasBasis = basis != null && Number.isFinite(Number(basis));
  const hasSettled = settled != null && Number.isFinite(Number(settled));

  if (hasSettled) {
    return {
      state: 'settled',
      // The arrow is the whole idea: it says a number moved and shows both ends.
      text: (hasBasis ? fmtCost(basis) + ' → ' : '') + fmtCost(settled),
      title: hasBasis
        ? 'Booked at ' + fmtCost(basis) + ' when received, settled at ' + fmtCost(settled) + ' by the vendor invoice'
        : 'No cost was known at receive. Settled at ' + fmtCost(settled) + ' by the vendor invoice',
    };
  }

  if (hasBasis) {
    return {
      state: 'provisional',
      text: fmtCost(basis),
      title: 'Provisional — this is what was assumed at receive. No vendor invoice has settled it yet.',
    };
  }

  return {
    state: 'none',
    text: '—',
    title: 'No cost was recorded for this movement.',
  };
}
